import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';

export interface GateOverridesType {
	[key: string]: boolean;
}

export interface ExperimentOverridesType {
	[key: string]: {
		[key: string]: boolean | string | number;
	};
}

function getQueryParams(): URLSearchParams {
	let search;
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		const { uri } = getSSRRenderInputs();
		search = (uri || '').split('?')[1] ?? '';
	} else {
		search = window.location.search;
	}
	return new URLSearchParams(search);
}

function parseParamGate(gate: string): [string, string | boolean] {
	const [rawGateName, rawGateValue] = gate.split(':');

	// Set default gate value to true
	let gateValue: boolean | string = rawGateValue ?? 'true';

	// Convert string values to real boolean values.
	if (gateValue.toLowerCase() === 'true') {
		gateValue = true;
	} else if (gateValue.toLowerCase() === 'false') {
		gateValue = false;
	}

	return [rawGateName, gateValue];
}

/*
 * Initialize overriding of feature gates based on query parameters from the URL
 * Supports both comma-separated and multiple param formats:
 * ?fg=<name>:<value>,<other name>:<other value>
 * ?fg=<name>:<value>&fg=<other name>:<other value>
 * For convenience, the value may be omitted (defaults to true):
 * ?fg=confluence_frontend_enable_feature is equivalent to ?fg=confluence_frontend_enable_feature:true
 * Note: We only return the overrides here (rather than all of the feature gates)
 */
export const processQueryParamFeatureGates = (): GateOverridesType | null => {
	const queryParams = getQueryParams();
	const fgParams = queryParams.getAll('fg');
	if (!fgParams.length) {
		return null;
	}

	const urlOverrides: GateOverridesType = {};
	let hasOverrides = false;
	for (const fgParam of fgParams) {
		for (const gate of fgParam.split(',')) {
			const [gateName, gateValue] = parseParamGate(gate);

			if (typeof gateValue === 'boolean') {
				urlOverrides[gateName] = gateValue;
				hasOverrides = true;
			}
		}
	}
	return hasOverrides ? urlOverrides : null;
};

export function parseParamExperiment(experiment: string): [string, string, string] | null {
	const parts = experiment.split(':');
	if (parts.length < 1 || !parts[0]) {
		return null;
	}
	const experimentName = parts[0];

	// If only 1 part (experimentName), default to isEnabled: true
	if (parts.length === 1) {
		return [experimentName, 'isEnabled', 'true'];
	}

	// If only 2 parts (experimentName:value), default to isEnabled property
	if (parts.length === 2) {
		return [experimentName, 'isEnabled', parts[1]];
	}

	// If 3+ parts (experimentName:property:value), use the property specified
	const property = parts[1];
	const rawValue = parts[2];

	return [experimentName, property, rawValue];
}

export function parseExperimentValue(value: string): boolean | string | number {
	// Try to parse as boolean
	if (value.toLowerCase() === 'true') {
		return true;
	}
	if (value.toLowerCase() === 'false') {
		return false;
	}

	// Try to parse as number
	const numValue = Number(value);
	if (!isNaN(numValue) && value.trim() !== '') {
		return numValue;
	}

	// Return as string
	return value;
}

/*
 * Initialize overriding of experiments based on query parameters from the URL
 * Simple format (defaults to isEnabled): ?exp=<name>:<value>, e.g.
 * ?exp=confluence_frontend_enable_feature:true (sets isEnabled: true)
 * ?exp=confluence_frontend_enable_feature (sets isEnabled: true)
 * Full format: ?exp=<name>:<property>:<value>, e.g.
 * ?exp=confluence_frontend_enable_feature:isEnabled:true
 * Multiple properties for the same experiment can be specified with multiple exp params:
 * ?exp=exp1:isEnabled:true&exp=exp1:otherProp:value
 * Or comma-separated: ?exp=exp1:isEnabled:true,exp1:otherProp:value
 * Multiple experiments: ?exp=exp1:true,exp2:false or ?exp=exp1:isEnabled:true,exp2:isEnabled:false
 * Note: We only return the overrides here (rather than all of the experiments)
 */
export const processQueryParamExperiments = (): ExperimentOverridesType | null => {
	const queryParams = getQueryParams();
	const experimentParams = queryParams.getAll('exp');
	if (!experimentParams.length) {
		return null;
	}

	const urlOverrides: ExperimentOverridesType = {};
	let hasOverrides = false;

	experimentParams.forEach((experimentParam: string) => {
		const experiments = experimentParam.split(',');
		experiments.forEach((experiment: string) => {
			const parsed = parseParamExperiment(experiment);
			if (parsed) {
				const [experimentName, property, rawValue] = parsed;
				const value = parseExperimentValue(rawValue);

				if (!urlOverrides[experimentName]) {
					urlOverrides[experimentName] = {};
				}
				urlOverrides[experimentName][property] = value;
				hasOverrides = true;
			}
		});
	});

	return hasOverrides ? urlOverrides : null;
};
